import { Decimal } from 'decimal.js';
import {
  FormField,
  createFieldCreator,
  createForm,
  validation as $,
} from './form-builder';
import { AssemblyTypeKey, RpCheck2Type } from '../../types/jobFormData';

export type TestFormContext = {
  formType: AssemblyTypeKey;
  rpCheck2Type: RpCheck2Type;
  check2Other: boolean;
};

type Siblings = Partial<{
  didNotOpen: number;
  reliefValveDripping: 'no' | 'upon_arrival' | 'upon_closure';
  openedAt: number;
}>;
const field = createFieldCreator<TestFormContext, Siblings>();

const isRP = (formType: AssemblyTypeKey) => formType.startsWith('RP');

const dischargingOnArrival = ({ reliefValveDripping }: Siblings) =>
  reliefValveDripping === 'upon_arrival';
const discharging = ({ reliefValveDripping }: Siblings) =>
  reliefValveDripping === 'upon_arrival' ||
  reliefValveDripping === 'upon_closure';

const roundTenth = (val: number) => Math.round(val * 10) / 10;

const validateSystemPsi = (val: number | undefined, required: boolean) => {
  if (val === undefined) {
    return required ? $.incomplete() : $.pass();
  } else if (val < 0) {
    return $.incomplete('Cannot be negative');
  } else if (val <= 25 || val >= 175) {
    return $.warn('This value falls outside normal operating values');
  } else {
    return $.pass();
  }
};

const validateCheck = (val: number | undefined) => {
  if (val === undefined) {
    return $.incomplete();
  } else if (val < 0) {
    return $.incomplete('Cannot be negative');
  } else if (val < 1) {
    return $.fail();
  } else if (val > 5) {
    return $.warn('Unusually high value');
  } else {
    return $.pass();
  }
};

const fields = {
  systemPsi: field.integer({
    label: 'System PSI',
    validate: (val) => validateSystemPsi(val, true),
  }),

  systemPsiOptional: field.integer({
    label: 'System PSI',
    validate: (val) => validateSystemPsi(val, false),
  }),

  check1: field.decimal({
    label: 'Check 1',
    transform: roundTenth,
    validate: validateCheck,
  }),

  check2: {
    DC: field.decimal({
      label: 'Check 2',
      transform: roundTenth,
      validate: validateCheck,
    }),

    RP: field.select(
      { Leaked: 'leaked', Tight: 'tight', _: 'other' },
      {
        label: 'Check 2',
        transform: (val, { context }) => (context.check2Other ? 'other' : val),
        validate(val, { check2Other }, siblings) {
          if (check2Other) {
            return $.hide();
          } else if (val === undefined) {
            return discharging(siblings) ? $.pass() : $.incomplete();
          } else if (val === 'leaked') {
            return $.fail();
          } else {
            return $.pass();
          }
        },
      }
    ),
  },

  check2Other: field.decimal({
    label: 'Check 2',
    transform: roundTenth,
    validate(val, { check2Other }, siblings) {
      if (!check2Other) {
        return $.hide();
      } else if (val === undefined) {
        return discharging(siblings) ? $.pass() : $.incomplete();
      } else if (val < 0) {
        return $.incomplete('Cannot be negative');
      } else if (val < 1) {
        return $.fail();
      } else {
        return $.pass();
      }
    },
  }),

  reliefValveDripping: field.select(
    {
      No: 'no',
      'Upon Arrival': 'upon_arrival',
      'Upon #2 Closure': 'upon_closure',
    },
    {
      label: 'Relief Valve Discharging',
      validate(val) {
        if (val === undefined) {
          return $.incomplete();
        } else if (val !== 'no') {
          return $.fail();
        } else {
          return $.pass();
        }
      },
    }
  ),

  openedAt: field.decimal({
    label: ({ formType }) =>
      isRP(formType) ? 'Relief Valve Opened At' : 'Air Inlet Opened At',
    transform: roundTenth,
    validate(val, { formType }, siblings) {
      if (siblings.didNotOpen) {
        return isRP(formType) ? $.hide() : $.pass();
      } else if (val === undefined) {
        return dischargingOnArrival(siblings) ? $.pass() : $.incomplete();
      } else if (val < 0) {
        return $.incomplete('Cannot be negative');
      } else if (val < (isRP(formType) ? 2 : 1)) {
        return $.fail();
      } else if (val >= 5) {
        return $.warn('Unusually high value');
      } else {
        return $.pass();
      }
    },
  }),

  didNotOpen: field.boolean<number>({
    label: ({ formType }) =>
      isRP(formType) ? 'Relief Valve Did Not Open' : 'Did Not Fully Open',
    toInputValue: (val) => val === 1,
    fromInputValue: (val) => (val ? 1 : 0),
    validate: (val) => (val ? $.fail() : $.pass()),
  }),

  pressureDrop: field.decimal({
    label: ({ formType }) =>
      isRP(formType) ? 'Check 1 Pressure Drop' : 'Check Valve',
    transform: roundTenth,
    validate(val, { formType, rpCheck2Type }, siblings) {
      if (val === undefined) {
        return dischargingOnArrival(siblings) ? $.pass() : $.incomplete();
      } else if (val < 0) {
        return $.incomplete('Cannot be negative');
      } else if (val < (isRP(formType) ? 5 : 1)) {
        return $.fail();
      }

      const { openedAt } = siblings;
      if (isRP(formType) && openedAt !== undefined && openedAt >= 0) {
        const buffer = new Decimal(val).minus(openedAt);
        if (rpCheck2Type === 'VALUE_BUFFER' && buffer.lessThan(3)) {
          return $.fail(
            'Must be at least 3 above the relief valve opening point'
          );
        }
        if (buffer.lessThan(0.1)) {
          return $.fail('Must be above the relief valve opening point');
        }
      }

      if (val > 12) {
        return $.warn('Unusually high value');
      }
      return $.pass();
    },
  }),

  bypassCheck: field.decimal({
    label: 'Bypass Check',
    transform: roundTenth,
    validate: validateCheck,
  }),
} as const;

export const formDC = createForm({
  systemPsi: fields.systemPsi,
  check1: fields.check1,
  check2: fields.check2.DC,
});

export const formRP = createForm({
  systemPsi: fields.systemPsi,
  reliefValveDripping: fields.reliefValveDripping,
  didNotOpen: fields.didNotOpen,
  openedAt: fields.openedAt,
  check2: fields.check2.RP,
  check2Other: fields.check2Other,
  pressureDrop: fields.pressureDrop,
});

export const formSvbPvb = createForm({
  systemPsi: fields.systemPsi,
  didNotOpen: fields.didNotOpen,
  openedAt: fields.openedAt,
  pressureDrop: fields.pressureDrop,
});

export const formType2Bypass = createForm({
  systemPsi: fields.systemPsiOptional,
  bypassCheck: fields.bypassCheck,
});

export const formAvbAirGap = createForm({
  systemPsi: fields.systemPsiOptional,
});

export type AllFormFields = {
  [key in keyof typeof fields]?: FormField<any, TestFormContext>;
};

export type AnyForm = {
  passing: boolean;
  completed: boolean;
  fields: AllFormFields;
};

export const emptyForm: AnyForm = {
  passing: false,
  completed: false,
  fields: {},
};
